import { useEffect, useMemo, useState } from 'react';
import {
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { Icone } from '../components/Icone';
import { usePerimetre } from '../site/usePerimetre';
import { useTaillePage } from '../lib/useTaillePage';
import { ErreurApi } from '../lib/api';
import { useCatalogue, type FormationCatalogue } from '../lib/useCatalogue';
import { useMarque } from '../marque/useMarque';

/**
 * Catalogue des formations (écran `catalog` de la maquette validée,
 * `docs/maquette/modules/03-pages-dashboard-catalogue.js`).
 *
 * La maquette montrait un catalogue de démonstration — formateurs, notes,
 * nombres d'inscrits et financements inventés. L'écran lit désormais
 * `GET /api/catalogue`, c'est-à-dire la table `Formation` installée depuis
 * `backend/src/referentiels/formations.ts`, et les effectifs comptés dans le
 * périmètre de sites de l'utilisateur.
 *
 * Ce que la plateforme ne connaît pas n'est plus affiché : il n'y a ni note de
 * satisfaction par formation, ni formateur titulaire, ni éligibilité au
 * financement en base. Les afficher au jugé aurait fait passer une maquette
 * pour une donnée de gestion.
 */

const TOUS_NIVEAUX = 'Tous les niveaux';

type Tri = 'titre' | 'heures' | 'effectif';

const TRIS: { valeur: Tri; libelle: string }[] = [
  { valeur: 'titre', libelle: 'Intitulé' },
  { valeur: 'heures', libelle: 'Volume horaire' },
  { valeur: 'effectif', libelle: 'Dossiers en cours' },
];

function CarteFormation({ formation }: { formation: FormationCatalogue }): JSX.Element {
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-card transition hover:-translate-y-1 hover:shadow-glow">
      <div className="relative flex h-32 items-center justify-center bg-gradient-to-br from-codialis-navy to-codialis-blue">
        <Icone nom="chapeauDiplome" className="h-12 w-12 text-white/90" />
        <span className="absolute left-3 top-3 rounded bg-white/90 px-2 py-1 font-mono text-[10px] font-bold text-codialis-navy backdrop-blur">
          {formation.code}
        </span>
        <span className="absolute right-3 top-3 rounded bg-white/90 px-2 py-1 text-[10px] font-bold text-codialis-navy backdrop-blur">
          {formation.niveau}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 sm:p-5">
        <h2 className="min-h-[2.6rem] [@media(pointer:coarse)]:min-h-[40px] font-display text-sm font-bold leading-snug text-codialis-navy line-clamp-2">
          {formation.titre}
        </h2>

        <dl className="grid grid-cols-2 gap-2 text-xs text-codialis-gray">
          <div>
            <dt className="font-semibold text-codialis-navy">Code diplôme</dt>
            <dd className="font-mono">{formation.codeDiplome}</dd>
          </div>
          <div>
            <dt className="font-semibold text-codialis-navy">RNCP</dt>
            <dd className="font-mono">{formation.rncp}</dd>
          </div>
        </dl>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-codialis-gray">
          <span className="flex items-center gap-1">
            <Icone nom="horloge" className="h-3 w-3" />
            {formation.heures} h au programme
          </span>
          {formation.heuresEcrits > 0 && <span>{formation.heuresEcrits} h d&apos;écrits</span>}
          {formation.heuresEcf > 0 && <span>{formation.heuresEcf} h d&apos;ECF</span>}
        </div>

        {formation.modules.length > 0 && (
          <div>
            <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
              Blocs de compétences
            </p>
            <ul className="space-y-1 text-xs text-codialis-gray">
              {formation.modules.map((module) => (
                <li key={module.libelle} className="flex gap-2">
                  <span aria-hidden className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-codialis-sky" />
                  <span>
                    {module.libelle}
                    {module.heures > 0 && ` · ${module.heures} h`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 text-xs text-codialis-gray">
          <Pastille
            texte={`${formation.dossiersEnCours} dossier${formation.dossiersEnCours > 1 ? 's' : ''} en cours`}
            ton="info"
          />
          <span>{formation.apprenantsEnFormation} en formation</span>
          {formation.sessionsOuvertes > 0 && (
            <span className="flex items-center gap-1">
              <Icone nom="calendrier" className="h-3 w-3" />
              {formation.sessionsOuvertes} session
              {formation.sessionsOuvertes > 1 ? 's' : ''} ouverte
              {formation.sessionsOuvertes > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

export function CataloguePage(): JSX.Element {
  const { site } = usePerimetre();
  const { marque } = useMarque();
  const { data, isLoading, error } = useCatalogue(site);

  const [niveau, setNiveau] = useState<string>(TOUS_NIVEAUX);
  const [recherche, setRecherche] = useState('');
  const [tri, setTri] = useState<Tri>('titre');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  useEffect(() => {
    setPage(1);
  }, [niveau, recherche, tri, taillePage, site]);

  const formations = useMemo(() => data?.formations ?? [], [data]);
  const niveaux = useMemo(() => data?.niveaux ?? [], [data]);

  // Le niveau retenu peut disparaître quand le périmètre change.
  useEffect(() => {
    if (niveau !== TOUS_NIVEAUX && niveaux.length > 0 && !niveaux.includes(niveau)) {
      setNiveau(TOUS_NIVEAUX);
    }
  }, [niveau, niveaux]);

  const visibles = useMemo(() => {
    const terme = recherche.trim().toLocaleLowerCase('fr');
    const retenues = formations.filter((formation) => {
      if (niveau !== TOUS_NIVEAUX && formation.niveau !== niveau) return false;
      if (!terme) return true;
      return [formation.titre, formation.code, formation.rncp, formation.codeDiplome]
        .join(' ')
        .toLocaleLowerCase('fr')
        .includes(terme);
    });

    return [...retenues].sort((a, b) => {
      if (tri === 'heures') return b.heures - a.heures;
      if (tri === 'effectif') return b.dossiersEnCours - a.dossiersEnCours;
      return a.titre.localeCompare(b.titre, 'fr');
    });
  }, [formations, niveau, recherche, tri]);

  const total = visibles.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const affichees = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return visibles.slice(debut, debut + taillePage);
  }, [visibles, page, taillePage]);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Catalogue de formations"
        sousTitre={`${formations.length} titre${formations.length > 1 ? 's' : ''} professionnel${formations.length > 1 ? 's' : ''} dispensé${formations.length > 1 ? 's' : ''} par ${marque.nom} · effectifs comptés sur le périmètre sélectionné`}
        actions={<BarreSites />}
      />

      {error && (
        <MessageErreur
          message={
            error instanceof ErreurApi ? error.message : "Le catalogue n'a pas pu être chargé."
          }
        />
      )}

      {isLoading && <Chargement libelle="Chargement du catalogue…" />}

      {niveaux.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {[TOUS_NIVEAUX, ...niveaux].map((libelle) => (
            <button
              key={libelle}
              type="button"
              aria-pressed={niveau === libelle}
              onClick={() => setNiveau(libelle)}
              className={`min-h-[40px] rounded-full px-4 py-1.5 text-sm font-medium transition ${
                niveau === libelle
                  ? 'bg-codialis-navy text-white'
                  : 'border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
              }`}
            >
              {libelle}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 text-sm shadow-card sm:flex-row sm:items-center">
        <label className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <Icone nom="recherche" className="h-4 w-4 text-codialis-gray" />
          <span className="sr-only">Rechercher une formation</span>
          <input
            type="search"
            value={recherche}
            onChange={(evenement) => setRecherche(evenement.target.value)}
            placeholder="Intitulé, code, RNCP…"
            className="w-full bg-transparent text-sm text-codialis-navy outline-none"
          />
        </label>
        <div className="flex items-center gap-2 text-xs text-codialis-gray">
          <label htmlFor="tri-catalogue">Tri :</label>
          <select
            id="tri-catalogue"
            value={tri}
            onChange={(evenement) => setTri(evenement.target.value as Tri)}
            className="min-h-[40px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-medium text-codialis-navy"
          >
            {TRIS.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </select>
        </div>
      </div>

      {affichees.length > 0 && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {affichees.map((formation) => (
            <CarteFormation key={formation.id} formation={formation} />
          ))}
        </div>
      )}

      {!isLoading && !error && total === 0 && (
        <p className="text-sm text-codialis-gray">
          Aucune formation ne correspond à cette recherche.
        </p>
      )}

      {total > 0 && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-card">
          <Pagination
            page={page}
            pages={pages}
            total={total}
            affiches={affichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="formation"
          />
        </div>
      )}
    </div>
  );
}
